import { type AppLauncherGlobalVariables } from '@src/views/app_launcher/AppLauncherGlobalVariables';
import { type Application } from '@vse/machinelogic';

// TS Placeholders
type Sequence = any; // vse/machinelogic/Sequence.js
type State = string; // vse/machinelogic/ApplicationState.js
type TraceData = any; // ApplicationStatus
type Command = any;

export type LaunchableAppExtension = 'mm' | '.py' | '.sh';

export interface LaunchableAppData {
  number: number
  name: string
  autoLaunch: boolean
  extension?: LaunchableAppExtension
  commands?: Command[]
  lastModifiedDate?: string
  machineLogicApplication?: Application
}

/**
 * Agnostically wraps both a Python App and a MachineMotion app in the same object
 */
export class LaunchableApp {
  appLauncherGlobalVariables: AppLauncherGlobalVariables;

  number: number;
  name: string;
  extension: LaunchableAppExtension;
  autoLaunch: boolean;
  commands?: Command[] | undefined;
  lastModifiedDate: string;
  machineLogicApplication?: Application | undefined;
  sequences: Sequence[];
  onTrace?: (traceData: TraceData) => void;
  onStateChanged?: (state: State) => void;
  state?: State;

  constructor (appLauncherGlobalVariables: AppLauncherGlobalVariables, launchableAppData: LaunchableAppData) {
    this.appLauncherGlobalVariables = appLauncherGlobalVariables;

    this.number = launchableAppData.number;
    this.name = launchableAppData.name;
    this.extension = launchableAppData.extension !== undefined ? launchableAppData.extension : 'mm';
    this.autoLaunch = launchableAppData.autoLaunch || false;
    this.commands = launchableAppData.commands;

    this.lastModifiedDate = launchableAppData.lastModifiedDate !== undefined && launchableAppData.lastModifiedDate !== ''
      ? launchableAppData.lastModifiedDate
      : '---';

    this.machineLogicApplication = launchableAppData.machineLogicApplication;

    if (this.machineLogicApplication !== undefined) {
      this.sequences = this.machineLogicApplication.mSequences;
    }
    else {
      this.sequences = [
        {
          name: 'Custom script',
          type: 'Sequence',
          parameters: {},
          instructions: [{ name: 'execute', category: 'trigger', parameters: {} }],
        },
      ];
    }
  }

  isPythonScript = (): boolean => this.extension !== 'mm';
  isMachineLogicApplication = (): boolean => this.extension === 'mm';

  getMachineLogicApplication = (): Application | undefined => this.machineLogicApplication;

  getOnTrace = (): ((traceData: TraceData) => void) | undefined => this.onTrace;
  setOnTrace = (pOnTrace: (traceData: TraceData) => void): void => {
    this.onTrace = pOnTrace;
  };

  setOnStateChanged = (pOnStateChanged: (newState: State) => void): void => {
    this.onStateChanged = pOnStateChanged;
  };

  setState = (pNewState: State): void => {
    if (this.state !== pNewState) {
      this.state = pNewState;
      this.onStateChanged?.(this.state);

      if (!this.isMachineLogicApplication()) {
        return;
      }

      const machineLogicApplication = this.appLauncherGlobalVariables.currentPlayingApp.getMachineLogicApplication();
      if (machineLogicApplication === undefined) {
        throw new Error('No machine logic application associated with the currently playing machine logic app. This is not expected and likely a development error.');
      }

      if (!machineLogicApplication.hasUi()) {
        return;
      }

      // Note (AJ): We never update the ML App state in the applauncher because the custom player widget interacts directly with the backend using
      // the appropriate network calls and only modifies the internal LaunchableApp.state, The UIB logic is dependant on the Application.mState so
      // for UIB Apps we update the ml application state along with the internal state.

      // @ts-expect-error TS is confused because this.mlApp.mState is in javascript
      this.machineLogicApplication.mState = pNewState;
    }
  };

  getName = (): string => this.name;
}
